import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Bell, User, LogOut, Settings, ChevronDown, Menu } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

/**
 * Top Navigation Bar Component
 * Shows app logo, user menu, notifications, and mobile menu toggle
 */
const Navbar = ({ onMenuToggle }) => {
  const { user, userProfile, logOut } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleSignOut = async () => {
    setShowUserMenu(false);
    await logOut();
  };

  if (!user) return null;

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 fixed w-full top-0 z-50">
      <div className="px-4 md:px-6 py-4">
        <div className="flex justify-between items-center">
          {/* Mobile menu button and Logo */}
          <div className="flex items-center space-x-3">
            {user && (
              <button
                onClick={onMenuToggle}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Toggle menu"
              >
                <Menu className="w-6 h-6 text-gray-600" />
              </button>
            )}

            <Link to="/dashboard" className="flex items-center space-x-2">
              <img
                src="/logo.png"
                alt="CRO Agent Logo"
                className="w-8 h-8"
                onError={(e) => {
                  // Fallback to letter logo if image not found
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div className="w-8 h-8 bg-primary-600 rounded-lg items-center justify-center hidden">
                <span className="text-white font-bold text-sm">P</span>
              </div>
              <span className="text-lg md:text-xl font-bold text-gray-900">
                CRO Agent
              </span>
            </Link>
          </div>

          {/* Right side menu */}
          <div className="flex items-center space-x-2 md:space-x-4">
            {/* Notifications */}
            <button className="relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center">
              <Bell className="w-5 h-5" />
              {userProfile?.stats?.unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {userProfile.stats.unreadNotifications > 9
                    ? "9+"
                    : userProfile.stats.unreadNotifications}
                </span>
              )}
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                data-testid="user-menu-button"
                className="flex items-center space-x-3 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {user.photoURL ? (
                  <img
                    src={user.photoURL}
                    alt={user.displayName || user.email}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-primary-600" />
                  </div>
                )}

                <div className="hidden md:block text-left">
                  <div className="text-sm font-medium text-gray-900">
                    {user.displayName || "User"}
                  </div>
                  <div className="text-xs text-gray-500">{user.email}</div>
                </div>

                <ChevronDown
                  className={`w-4 h-4 text-gray-500 transition-transform ${showUserMenu ? "rotate-180" : ""}`}
                />
              </button>

              {/* Dropdown menu */}
              {showUserMenu && (
                <>
                  {/* Backdrop */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowUserMenu(false)}
                  />

                  {/* Menu */}
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                    <div className="py-2">
                      {/* User info */}
                      <div className="px-4 py-2 border-b border-gray-100">
                        <div className="text-sm font-medium text-gray-900">
                          {user.displayName || "User"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {user.email}
                        </div>
                        {userProfile?.stats && (
                          <div className="text-xs text-gray-500 mt-1">
                            {userProfile.stats.activeConnections} connections,{" "}
                            {userProfile.stats.totalGoals} goals
                          </div>
                        )}
                      </div>

                      {/* Menu items */}
                      <Link
                        to="/settings"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Settings className="w-4 h-4 mr-3" />
                        Settings
                      </Link>

                      <button
                        onClick={handleSignOut}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
